/**
 * FP3-105 — Super Admin permanent deletion modal (school OR organization).
 *
 * Flow: server preview (exact counts + warnings + blockers + backup
 * disclosure) → strict confirmation (exact target name + exact phrase
 * + acknowledgment checkbox) → apply creates a resumable server-side
 * job → live stage progress polling → completed / failed (+ Resume).
 *
 * The server re-validates name, phrase, and material counts — the
 * client gates here are UX, not the security boundary.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const COUNT_LABELS = {
  schools: "Schools / campuses",
  students_total: "Students (total)",
  students_active: "Students · active",
  students_archived: "Students · archived",
  students_soft_deleted: "Students · soft-deleted",
  users: "User accounts",
  admins: "Org admins",
  providers: "Providers",
  provider_users: "Provider accounts",
  assistants: "Assistants",
  school_only_users: "Users only at this school",
  shared_users: "Users shared with other schools",
  providers_assigned_to_school: "Providers assigned here",
  assistants_assigned_to_school: "Assistants assigned here",
  pending_invites: "Pending invites",
  documents: "Stored documents (files)",
  goals: "Goals",
  goal_evidence: "Goal evidence",
  sessions: "Sessions",
  notes: "Notes",
  assessments: "Assessments",
  trial_runs: "Data collection runs",
  messages: "Messages",
};

const PRIMARY_KEYS = {
  school: [
    "students_total", "school_only_users", "shared_users",
    "providers_assigned_to_school", "assistants_assigned_to_school",
    "pending_invites", "documents",
  ],
  organization: [
    "schools", "students_total", "users", "admins", "providers",
    "assistants", "pending_invites", "documents",
  ],
};

const STAGE_LABELS = {
  access_locked: "Lock access",
  auth_revoked: "Revoke invites & reset tokens",
  files_deleted: "Delete stored files",
  student_records_deleted: "Delete student records",
  provider_records_deleted: "Delete provider records",
  provider_user_cleanup: "Remove school-only providers & users",
  user_records_deleted: "Delete user accounts",
  school_records_deleted: "Delete school records",
  organization_deleted: "Delete organization",
  orphan_verification: "Verify no records remain",
  complete: "Complete",
};

const errDetail = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") {
    if (Array.isArray(d.blockers)) return d.blockers.join(" ");
    return d.message || JSON.stringify(d);
  }
  return e?.message || "Request failed";
};

export default function PlatformDeleteModal({
  open,
  onOpenChange,
  targetType, // "school" | "organization"
  target, // { id, name, ... }
  asOrg,
  onDeleted,
}) {
  const isOrg = targetType === "organization";
  const noun = isOrg ? "organization" : "school";

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [ack, setAck] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [job, setJob] = useState(null);
  const [resuming, setResuming] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setPreviewError("");
      setConfirmName("");
      setConfirmPhrase("");
      setAck(false);
      setShowAll(false);
      setApplyError("");
      setJob(null);
      return;
    }
    setLoadingPreview(true);
    const req = isOrg
      ? api.platformOrgDeletePreview(target.id)
      : api.platformSchoolDeletePreview(target.id, asOrg ? { as_org: asOrg } : {});
    req
      .then((r) => setPreview(r.data))
      .catch((e) => setPreviewError(errDetail(e)))
      .finally(() => setLoadingPreview(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll the resumable job while it runs.
  useEffect(() => {
    if (!job?.id || job.status !== "running") return undefined;
    pollRef.current = setInterval(() => {
      api
        .platformDeletionJob(job.id)
        .then((r) => setJob(r.data))
        .catch(() => {});
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [job?.id, job?.status]);

  const nameOk = confirmName.trim() === (target?.name || "").trim();
  const phraseOk =
    preview && confirmPhrase.trim() === preview.confirmation_phrase;
  const blockers = preview?.blockers || [];
  const canApply =
    !!preview && blockers.length === 0 && nameOk && phraseOk && ack && !applying;

  const apply = async () => {
    setApplying(true);
    setApplyError("");
    const body = {
      preview_token: preview.preview_token,
      confirm_name: confirmName.trim(),
      confirm_phrase: confirmPhrase.trim(),
      expected_counts: preview.counts,
    };
    try {
      const r = isOrg
        ? await api.platformOrgDeleteApply(target.id, body)
        : await api.platformSchoolDeleteApply(
            target.id, body, asOrg ? { as_org: asOrg } : {});
      const d = r.data || {};
      if (d.idempotent && d.job) {
        setJob(d.job);
      } else {
        setJob({ id: d.job_id, status: "running", stages: d.stages || [], stages_completed: [], current_stage: (d.stages || [])[0] });
      }
    } catch (e) {
      setApplyError(errDetail(e));
    } finally {
      setApplying(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    try {
      await api.platformDeletionJobResume(job.id);
      setJob((j) => ({ ...j, status: "running", error: null }));
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setResuming(false);
    }
  };

  const handleOpenChange = (o) => {
    if (!o && job?.status === "running") {
      toast.info("Deletion continues in the background.");
    }
    onOpenChange(o);
  };

  const counts = preview?.counts || {};
  const primaryKeys = PRIMARY_KEYS[isOrg ? "organization" : "school"];
  const otherEntries = Object.entries(counts)
    .filter(([k, v]) => !primaryKeys.includes(k) && Number(v) > 0)
    .sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[88vh] overflow-y-auto"
        data-testid="platform-delete-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-[#7A1E1E]">
            Permanently delete {noun}
          </DialogTitle>
          <DialogDescription data-testid="platform-delete-warning">
            {isOrg
              ? "This permanently deletes the organization and everything inside it — schools, students, providers, admins, records, and stored files. Every account in this organization immediately loses access."
              : "This permanently deletes the school and every record scoped exclusively to it. Users who only belong to this school lose their accounts; users shared with other schools keep their remaining access."}{" "}
            This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {/* ── Job progress replaces the confirmation UI once running ── */}
        {job ? (
          <JobProgress
            job={job}
            noun={noun}
            targetName={target?.name || ""}
            resuming={resuming}
            onResume={resume}
            onDone={() => onDeleted?.(job)}
          />
        ) : (
          <>
            {loadingPreview && (
              <p className="text-[12px] text-[#6B7280]" data-testid="platform-delete-loading">
                Calculating exactly what will be deleted…
              </p>
            )}
            {previewError && (
              <p className="text-[12px] text-[#B85C5C]" data-testid="platform-delete-preview-error">
                {previewError}
              </p>
            )}

            {preview && (
              <div className="space-y-3">
                <div
                  className="rounded-md border border-[#EAD5D0] bg-[#FBF3F1] p-3"
                  data-testid="platform-delete-preview-counts"
                >
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-[#7A1E1E] mb-1.5">
                    Will be permanently deleted — {target?.name}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {primaryKeys.map((k) => (
                      <div
                        key={k}
                        className="flex items-center justify-between text-[11.5px]"
                        data-testid={`platform-delete-count-${k}`}
                      >
                        <span className="text-[#3F4756]">{COUNT_LABELS[k] || k}</span>
                        <span className="font-semibold tabular-nums text-[#1F2937]">
                          {Number(counts[k] ?? 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                  {otherEntries.length > 0 && (
                    <button
                      type="button"
                      data-testid="platform-delete-toggle-all-counts"
                      onClick={() => setShowAll((v) => !v)}
                      className="mt-2 text-[10.5px] text-[#7A1E1E] underline underline-offset-2"
                    >
                      {showAll ? "Hide" : "Show"} all record counts ({otherEntries.length})
                    </button>
                  )}
                  {showAll && (
                    <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="platform-delete-all-counts">
                      {otherEntries.map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between text-[10.5px] text-[#6B7280]">
                          <span>{COUNT_LABELS[k] || k.replace(/_/g, " ")}</span>
                          <span className="tabular-nums">{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {(preview.warnings || []).length > 0 && (
                  <ul
                    className="space-y-1 rounded-md border border-[#E0CFA8]/60 bg-[#FDF8F3] px-2.5 py-2"
                    data-testid="platform-delete-warnings"
                  >
                    {preview.warnings.map((w, i) => (
                      <li key={i} className="text-[10.5px] text-[#9C6B2E]">
                        • {w}
                      </li>
                    ))}
                  </ul>
                )}

                {blockers.length > 0 && (
                  <ul
                    className="space-y-1 rounded-md border border-[#EAD5D0] bg-[#FBF3F1] px-2.5 py-2"
                    data-testid="platform-delete-blockers"
                  >
                    {blockers.map((b, i) => (
                      <li key={i} className="text-[11px] font-medium text-[#B85C5C]">
                        ✕ {b}
                      </li>
                    ))}
                  </ul>
                )}

                {blockers.length === 0 && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-[11px]">
                        Type the exact {noun} name:{" "}
                        <span className="font-mono font-semibold">{target?.name}</span>
                      </Label>
                      <Input
                        data-testid="platform-delete-name-input"
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        className="h-8 text-[12px]"
                        placeholder={target?.name}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px]">
                        Type{" "}
                        <span className="font-mono font-semibold">
                          {preview.confirmation_phrase}
                        </span>{" "}
                        to confirm
                      </Label>
                      <Input
                        data-testid="platform-delete-phrase-input"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value)}
                        className="h-8 text-[12px] font-mono"
                        placeholder={preview.confirmation_phrase}
                        autoComplete="off"
                      />
                    </div>
                    <label className="flex items-start gap-2 text-[11.5px] text-[#3F4756] cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="platform-delete-ack-checkbox"
                        checked={ack}
                        onChange={(e) => setAck(e.target.checked)}
                        className="mt-0.5 accent-[#7A1E1E]"
                      />
                      <span>
                        I understand this permanently deletes the data above and
                        cannot be undone.
                      </span>
                    </label>
                  </>
                )}

                {applyError && (
                  <p className="text-[12px] text-[#B85C5C]" data-testid="platform-delete-apply-error">
                    {applyError}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {!job && (
          <DialogFooter>
            <Button
              data-testid="platform-delete-cancel-btn"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="platform-delete-apply-btn"
              size="sm"
              disabled={!canApply}
              onClick={apply}
              className="bg-[#7A1E1E] hover:bg-[#611414] text-white disabled:opacity-40"
            >
              {applying ? "Starting…" : `Permanently delete ${noun}`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function JobProgress({ job, noun, targetName, resuming, onResume, onDone }) {
  const stages = job.stages || [];
  const done = new Set(job.stages_completed || []);
  const completed = job.status === "completed";
  const failed = job.status === "failed";
  return (
    <div className="space-y-3" data-testid="platform-delete-job-progress">
      <p className="text-[12px] text-[#3F4756]">
        {completed
          ? `“${targetName}” has been permanently deleted.`
          : failed
            ? "The deletion job stopped before completing. No success is reported — you can resume it safely (all steps are idempotent)."
            : `Deleting “${targetName}”… This runs server-side and continues even if you close this dialog.`}
      </p>
      <ul className="space-y-1 rounded-md border border-[#E5E3DB] bg-[#FBFAF6] px-3 py-2.5">
        {stages.map((s) => {
          const isDone = done.has(s) || completed;
          const isCurrent = !isDone && job.current_stage === s && job.status === "running";
          return (
            <li
              key={s}
              data-testid={`platform-delete-stage-${s}`}
              className={`flex items-center gap-2 text-[11.5px] ${
                isDone ? "text-[#1E5A3F]" : isCurrent ? "text-[#1F2937] font-medium" : "text-[#9AA0A6]"
              }`}
            >
              <span className="w-4 text-center">
                {isDone ? "✓" : isCurrent ? "…" : "·"}
              </span>
              {STAGE_LABELS[s] || s}
            </li>
          );
        })}
      </ul>
      {failed && (
        <div
          className="rounded-md border border-[#EAD5D0] bg-[#FBF3F1] px-2.5 py-2"
          data-testid="platform-delete-job-failed"
        >
          <p className="text-[11px] font-medium text-[#B85C5C]">
            {job.error || "Deletion failed."}
          </p>
          {job.orphan_report && Object.keys(job.orphan_report).length > 0 && (
            <p className="text-[10.5px] text-[#6B7280] mt-1">
              Remaining records:{" "}
              {Object.entries(job.orphan_report)
                .map(([k, v]) => `${k}: ${v}`)
                .join(" · ")}
            </p>
          )}
        </div>
      )}
      {completed && (
        <p className="text-[11px] text-[#1E5A3F]" data-testid="platform-delete-job-completed">
          Verified: no scoped records remain. A minimal audit record (no
          student data) was retained.
        </p>
      )}
      <DialogFooter>
        {failed && (
          <Button
            data-testid="platform-delete-resume-btn"
            size="sm"
            onClick={onResume}
            disabled={resuming}
            className="bg-[#7A1E1E] hover:bg-[#611414] text-white"
          >
            {resuming ? "Resuming…" : "Resume deletion"}
          </Button>
        )}
        {completed && (
          <Button
            data-testid="platform-delete-done-btn"
            size="sm"
            onClick={onDone}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            Done
          </Button>
        )}
      </DialogFooter>
    </div>
  );
}
